import { useState } from "react";
import { Copy, Check, ExternalLink, Code, Mail, Globe } from "lucide-react";
import { toast } from "sonner";

/**
 * /embed — Self-serve embed kit for the partnership-lead team.
 * Lists all three lead-form variants with copy-to-clipboard buttons.
 */

const ORIGIN =
  (typeof window !== "undefined" && window.location ? window.location.origin : "https://ultra-net-worth.emergent.host");

const VARIANTS = [
  {
    id: "direct",
    icon: Globe,
    title: "Direct Link",
    description: "Send the React-styled lead form URL in an email signature, social bio, or QR code. Append UTM params to attribute the campaign.",
    use_cases: ["Email signatures", "QR codes on print collateral", "Social media bios", "DM follow-ups"],
    code_label: "URL (with UTM example)",
    code: `${ORIGIN}/lead?utm_source=email&utm_medium=signature&utm_campaign=feb_briefing`,
    code_lang: "txt",
  },
  {
    id: "iframe",
    icon: Code,
    title: "Iframe Embed",
    description: "Drop a single iframe tag into any landing page, third-party host, or partner microsite — brand-styled, dark luxe. Append UTM params to the src URL to track the campaign.",
    use_cases: ["Partner microsites", "Landing-page builders (Webflow, Carrd, Squarespace)", "Notion/Coda dashboards", "PDF QR-code landings"],
    code_label: "Iframe HTML",
    code: `<iframe
  src="${ORIGIN}/lead-embed.html?utm_source=partner&utm_medium=microsite&utm_campaign=feb_2026"
  width="100%"
  height="780"
  frameborder="0"
  style="border:0;max-width:560px;display:block;margin:0 auto"
  title="WEALTH FIRST INC. — Lead Form"
  loading="lazy"
></iframe>`,
    code_lang: "html",
  },
  {
    id: "snippet",
    icon: Mail,
    title: "Drop-in Code Snippet",
    description: "Copy/paste the raw HTML+JS into any existing same-origin page. Zero styling, zero dependencies — your developer wraps it in their own design.",
    use_cases: ["Developer-built landing pages", "Mailchimp / Resend email landings", "Internal portals", "WordPress / Webflow embed blocks"],
    code_label: "HTML + JS",
    code: `<form id="leadForm">
  <input name="name" type="text" placeholder="Full Name" required />
  <input name="email" type="email" placeholder="Email" required />
  <input name="company" type="text" placeholder="Company" />
  <textarea name="message" placeholder="Message" required></textarea>
  <button type="submit">Submit</button>
</form>

<script>
  document.getElementById('leadForm').addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const qs = new URLSearchParams(window.location.search);
    const payload = {
      name: formData.get('name'),
      email: formData.get('email'),
      company: formData.get('company'),
      message: formData.get('message'),
      source: 'lead_form_snippet',
      utm_source:   qs.get('utm_source')   || '',
      utm_medium:   qs.get('utm_medium')   || '',
      utm_campaign: qs.get('utm_campaign') || '',
      utm_term:     qs.get('utm_term')     || '',
      utm_content:  qs.get('utm_content')  || '',
      referrer:     document.referrer      || '',
      landing_path: window.location.pathname + window.location.search
    };
    const res = await fetch('${ORIGIN}/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      form.reset();
      alert('Submitted successfully');
    } else {
      alert('Submission failed');
    }
  });
</script>`,
    code_lang: "html",
  },
];

export default function Embed() {
  return (
    <div data-testid="embed-page" className="newsletter-theme pt-24 pb-24" style={{ background: "var(--bg)", minHeight: "100vh" }}>
      <section className="px-6 pt-8 pb-12 max-w-5xl mx-auto">
        <p className="newsletter-eyebrow mb-4" style={{ color: "var(--brass)" }}>
          Partnership · Embed Kit
        </p>
        <h1 className="newsletter-display text-5xl md:text-6xl mb-5 leading-[0.95]">
          Three ways to <em style={{ color: "var(--brass)" }}>capture leads</em>.
        </h1>
        <p className="newsletter-body text-base md:text-lg max-w-3xl" style={{ color: "var(--ink-muted)", lineHeight: 1.65 }}>
          Pick the variant that fits your channel — every option writes to the same WealthFirst leads database, fires the
          same email notification to <span style={{ color: "var(--brass)" }}>support@wealthfirstinc.tv</span>, and lands in the same admin pipeline.
        </p>
      </section>

      <section className="px-6 max-w-5xl mx-auto space-y-6">
        {VARIANTS.map((v) => (
          <VariantCard key={v.id} variant={v} />
        ))}
      </section>

      <section className="px-6 max-w-5xl mx-auto mt-16 pt-12" style={{ borderTop: "1px solid var(--hairline)" }}>
        <p className="newsletter-eyebrow mb-4" style={{ color: "var(--brass)" }}>UTM Attribution</p>
        <h2 className="newsletter-display text-3xl md:text-4xl mb-6 leading-tight">
          Every lead is <em style={{ color: "var(--brass)" }}>auto-tagged</em> with campaign source.
        </h2>
        <p className="newsletter-body text-base mb-6 max-w-3xl" style={{ color: "var(--ink-muted)", lineHeight: 1.65 }}>
          All three variants automatically read <code style={{ color: "var(--brass)" }}>?utm_source</code>,{" "}
          <code style={{ color: "var(--brass)" }}>?utm_medium</code>, <code style={{ color: "var(--brass)" }}>?utm_campaign</code>,{" "}
          <code style={{ color: "var(--brass)" }}>?utm_term</code>, and <code style={{ color: "var(--brass)" }}>?utm_content</code> from the page URL and post them to the leads database along with{" "}
          <code style={{ color: "var(--brass)" }}>document.referrer</code> and the landing path. No additional code needed.
        </p>
        <div className="p-6" style={{ border: "1px solid var(--hairline-strong)", background: "var(--surface)" }}>
          <p className="newsletter-eyebrow mb-3" style={{ color: "var(--brass)" }}>Example campaign tags</p>
          <pre className="text-xs leading-relaxed overflow-x-auto" style={{ color: "var(--ink-muted)", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
{`Email signature   → utm_source=email · utm_medium=signature
Newsletter footer → utm_source=newsletter · utm_medium=email · utm_campaign=feb_briefing
Partner microsite → utm_source=hermes · utm_medium=partner · utm_campaign=ss26_couture
LinkedIn post     → utm_source=linkedin · utm_medium=social · utm_campaign=family_office_q1
Paid LinkedIn ad  → utm_source=linkedin · utm_medium=cpc · utm_campaign=uhnw_funding · utm_content=variant_a`}
          </pre>
        </div>
      </section>

      <section className="px-6 max-w-5xl mx-auto mt-16 pt-12" style={{ borderTop: "1px solid var(--hairline)" }}>
        <p className="newsletter-eyebrow mb-4" style={{ color: "var(--brass)" }}>API Contract</p>
        <h2 className="newsletter-display text-3xl md:text-4xl mb-6 leading-tight">
          All three variants POST to the same endpoint.
        </h2>
        <div className="p-6" style={{ border: "1px solid var(--hairline-strong)", background: "var(--surface)" }}>
          <p className="newsletter-eyebrow mb-3" style={{ color: "var(--brass)" }}>POST {ORIGIN}/api/leads</p>
          <pre className="text-xs leading-relaxed overflow-x-auto" style={{ color: "var(--ink-muted)", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
{`{
  "name":         "string (2-120 chars, required)",
  "email":        "valid email (required)",
  "company":      "string (optional)",
  "message":      "string (4-2000 chars, required)",
  "source":       "string (optional — e.g. 'lead_form_embed', 'lead_form_snippet')",
  "utm_source":   "string (auto-captured from URL)",
  "utm_medium":   "string (auto-captured)",
  "utm_campaign": "string (auto-captured)",
  "utm_term":     "string (auto-captured)",
  "utm_content":  "string (auto-captured)",
  "referrer":     "string (auto from document.referrer)",
  "landing_path": "string (auto from window.location)"
}

→ 200 OK { "ok": true, "id": "uuid", "status": "received" }
→ 422    validation errors (Pydantic)`}
          </pre>
        </div>
      </section>
    </div>
  );
}

function VariantCard({ variant }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(variant.code);
      setCopied(true);
      toast.success(`${variant.title} copied to clipboard.`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = variant.code;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success(`${variant.title} copied to clipboard.`);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Copy failed — please select and copy manually.");
      }
      document.body.removeChild(ta);
    }
  };

  return (
    <div
      data-testid={`embed-variant-${variant.id}`}
      className="p-7"
      style={{ border: "1px solid var(--hairline-strong)", background: "var(--surface)" }}
    >
      <div className="flex items-start gap-4 mb-5">
        <variant.icon size={28} style={{ color: "var(--brass)", flexShrink: 0, marginTop: 4 }} />
        <div className="flex-1 min-w-0">
          <h3 className="newsletter-display text-2xl md:text-3xl mb-2 leading-tight">{variant.title}</h3>
          <p className="newsletter-body text-sm md:text-base leading-relaxed" style={{ color: "var(--ink-muted)" }}>
            {variant.description}
          </p>
        </div>
      </div>

      <div className="mb-4">
        <p className="newsletter-eyebrow mb-2">Best for</p>
        <div className="flex flex-wrap gap-2">
          {variant.use_cases.map((u) => (
            <span
              key={u}
              className="text-xs px-2.5 py-1"
              style={{
                border: "1px solid var(--hairline-strong)",
                color: "var(--ink-muted)",
                letterSpacing: "0.05em",
              }}
            >
              {u}
            </span>
          ))}
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <p className="newsletter-eyebrow" style={{ color: "var(--brass)" }}>{variant.code_label}</p>
        <button
          type="button"
          onClick={copy}
          data-testid={`embed-copy-${variant.id}`}
          className="text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2"
          style={{
            border: `1px solid ${copied ? "var(--brass)" : "var(--hairline-strong)"}`,
            background: copied ? "rgba(194,163,115,0.1)" : "transparent",
            color: copied ? "var(--brass)" : "var(--ink-muted)",
            padding: "8px 14px",
            cursor: "pointer",
            transition: "all 150ms ease",
            minHeight: 36,
          }}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <pre
        data-testid={`embed-code-${variant.id}`}
        className="text-xs leading-relaxed overflow-x-auto p-4"
        style={{
          background: "var(--bg)",
          border: "1px solid var(--hairline)",
          color: "var(--ink)",
          fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
          maxHeight: 360,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {variant.code}
      </pre>

      {variant.id !== "snippet" && (
        <div className="mt-4">
          <a
            href={variant.id === "direct" ? "/lead" : "/lead-embed.html"}
            target="_blank"
            rel="noreferrer"
            data-testid={`embed-preview-${variant.id}`}
            className="text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2"
            style={{ color: "var(--brass)", textDecoration: "none", borderBottom: "1px solid var(--brass)", paddingBottom: 2 }}
          >
            Preview live <ExternalLink size={11} />
          </a>
        </div>
      )}
    </div>
  );
}
